import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { QueryObserverResult } from 'react-query';
import { useRouter } from 'next/router';
import { useToast } from '@common/toast';
import { EduStateType } from '@components/profile/profileOverlay/overlays/edu';
import { useSaveProfileEducation } from '@repository/profiles/useProfileEducation';
import Major from '@components/profile/profileOverlay/overlays/edu/eduForm/Major';
import Minor from '@components/profile/profileOverlay/overlays/edu/eduForm/Minor';
import Double from '@components/profile/profileOverlay/overlays/edu/eduForm/Double';
import StartDate from '@components/profile/profileOverlay/overlays/edu/eduForm/StartDate';
import EndDate from '@components/profile/profileOverlay/overlays/edu/eduForm/EndDate';
import SchoolLevel from '@components/profile/profileOverlay/overlays/edu/eduForm/SchoolLevel';
import School from '@components/profile/profileOverlay/overlays/edu/eduForm/School';
import EduType from '@components/profile/profileOverlay/overlays/edu/eduForm/EduType';
import Research from '@components/profile/profileOverlay/overlays/edu/eduForm/Research';
import { useMatchProfile } from '@repository/match';
import eduFormSchema from './eduFormSchema';
import { ProfileEducationUpdateVO, ProfileResearchUpdateVO } from '@domain/rq/profile/ProfileEducationRq';
import { SourcePage } from '@domain/rq/DataLog';
import { ProfileEducationFormVO, ProfileMajorFormVO } from '@domain/vo/profile/ProfileEducationFormVO';
import ProfileEducationVO, { EducationVO } from '@domain/vo/profile/ProfileEducationVO';
import { KeyboardEventUtil } from '@utils/KeyboardEventUtil';

import classnames from 'classnames/bind';
import styles from '../edu.module.scss';
const cx = classnames.bind(styles);

export const defaultMajor: ProfileMajorFormVO = {
  majorAlias: null,
  majorType: 'MAJOR',
  scoreType: 'SCORE',
  score: '',
  perfectScore: '',
};

export const defaultResearch: ProfileResearchUpdateVO = {
  attachFiles: [],
  description: '',
};

const eduFormDefaultValues: ProfileEducationFormVO = {
  schoolLevel: null, // 학교 구분
  school: null, // 학교
  educationType: null, // 상태
  transferYn: false, //편입 상태
  startDate: '', // 재학 기간 시작
  endDate: '', // 재학 기간 종료
  majorUseYn: false,
  major: {
    // 주전공
    majorAlias: null,
    majorType: 'MAJOR',
    scoreType: 'SCORE',
    score: '',
    perfectScore: '',
  },
  minorUseYn: false,
  minor: {
    // 부전공
    majorAlias: null,
    majorType: 'MINOR',
    scoreType: 'SCORE',
    score: '',
    perfectScore: '',
  },
  doubleUseYn: false,
  double: {
    // 복수 전공
    majorAlias: null,
    majorType: 'DOUBLE',
    scoreType: 'SCORE',
    score: '',
    perfectScore: '',
  },
  profileResearches: [defaultResearch], // 연구 분야 논문
};

interface EduFormProps {
  refetch: () => Promise<QueryObserverResult>;
  profileEducation?: ProfileEducationVO;
  education: EducationVO | null;
  educationIndex: number | null;
  setEduState: (eduState: EduStateType) => void;
  openExitModal: () => void;
}

const EduForm = forwardRef(
  ({ refetch, profileEducation, education, educationIndex, setEduState, openExitModal }: EduFormProps, ref) => {
    const { query } = useRouter();

    useImperativeHandle(ref, () => ({
      getIsDirty,
    }));

    const { refetch: matchProfileRefetch } = useMatchProfile();

    const methods = useForm<ProfileEducationFormVO>({
      mode: 'onChange',
      defaultValues: eduFormDefaultValues as ProfileEducationFormVO,
      resolver: yupResolver(eduFormSchema) as any,
    });

    const { handleSubmit, control, reset, formState } = methods;
    const schoolLevel = useWatch<any>({ name: 'schoolLevel', control });
    const educationType = useWatch<any>({ name: 'educationType', control });
    const Toast = useToast();

    const isHighSchool = schoolLevel?.value === 'HIGHSCHOOL';

    const { mutate: saveProfileEducation } = useSaveProfileEducation(() => {
      refetch().then(() => {
        Toast({ type: 'success', iconType: 'info', content: '성공적으로 저장되었습니다.' });
        setEduState('LIST');
        //SNb 완성 percent 업데이트
        matchProfileRefetch();
      });
    });

    useEffect(() => {
      if (education) reset({ ...ProfileEducationFormVO.convertProfileEducationFormVO(education) });
    }, [education]);

    const getIsDirty = (): boolean => {
      return Object.keys(formState.dirtyFields).length !== 0;
    };

    const handlePrev = () => {
      if (isHighSchool && formState.isDirty) {
        openExitModal();
        return;
      }

      if (!isHighSchool && Object.keys(formState.dirtyFields).length !== 0) {
        openExitModal();
        return;
      }

      setEduState('LIST');
    };

    const onSubmit: SubmitHandler<ProfileEducationFormVO> = async (vo) => {
      const getEducations = () => {
        const arr =
          profileEducation?.educations?.map(
            (edu) => new ProfileEducationUpdateVO({ ...edu, sourcePage: query.source_page as SourcePage }),
          ) || [];
        const newEdu = ProfileEducationFormVO.convertProfileEducationUpdateVO(vo);

        if (educationIndex === null) return [...arr, newEdu];
        else
          return arr.map((edu, index) => {
            if (index === educationIndex) return newEdu;
            else return edu;
          });
      };

      saveProfileEducation({ educations: getEducations(), sourcePage: query.source_page as SourcePage });
    };

    const onSubmitError = () => {
      Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
    };

    return (
      <FormProvider {...methods}>
        <form
          id="eduForm"
          className={cx('scrollArea')}
          onSubmit={(v) => handleSubmit(onSubmit, onSubmitError)(v)}
          onKeyDown={KeyboardEventUtil.onCheckKeyDownPreventEnterExceptTextarea}
        >
          <div className={cx('scrollInner')}>
            <div className={cx('scrollWrap')}>
              <div className={cx('writeArea')}>
                <div className={cx('writeInner')}>
                  <span className={cx('writeText', 'required')}>학교 구분</span>
                  {/* 학교 구분 */}
                  <SchoolLevel name="schoolLevel" />
                </div>

                <div className={cx('writeInner')}>
                  <span className={cx('writeText', 'required')}>학교명</span>
                  {/* 학교 명, 학교 코드 */}
                  <School name="school" schoolLevel={schoolLevel} />
                </div>

                <div className={cx('writeInner')}>
                  <span className={cx('writeText', 'required')}>상태</span>
                  {/* 상태 */}
                  <EduType name="educationType" />
                </div>

                <div className={cx('writeInner')}>
                  <span className={cx('writeText', 'required')}>재학 기간</span>
                  {/* 재학 기간 */}
                  <div className={cx('periodArea')}>
                    <StartDate name="startDate" />
                    <mark>~</mark>
                    <EndDate name="endDate" educationType={educationType} />
                  </div>
                </div>

                {!isHighSchool && (
                  <div className={cx('writeInner')}>
                    <span className={cx('writeText', 'required')}>학과·전공</span>
                    {/* 학과·전공 */}
                    <div className={cx('majorArea')}>
                      <Major name={'major'} schoolLevel={schoolLevel} />
                      <Minor name={'minor'} />
                      <Double name={'double'} />
                    </div>
                  </div>
                )}

                {!isHighSchool && (
                  <div className={cx('writeInner')}>
                    {/* 연구 분야·논문 */}
                    <Research control={control} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>

        <div className={cx('btnArea')}>
          <a className={cx('btn', 'cancel')} role="button" onClick={handlePrev}>
            이전
          </a>
          <button className={cx('btn', 'confirm')} type="submit" form="eduForm">
            등록
          </button>
        </div>
      </FormProvider>
    );
  },
);

export default EduForm;
